import { isGroup, type Condition, type FormField, type Rule } from '@re/form-engine';
import type { Lookups } from '@re/form-ui';
import { dimFields } from './visibility';

/**
 * "By case" editing: a field is shown or hidden per (request type × property type).
 * We read the field's rule into that table, change some cells, and write the table back as
 * the smallest rule of the same kind the engine already runs:
 *   all: [ request_type in …, category in … | property_type in … ]   (one group)
 *   any: [ {all: […]}, {all: […]} ]                                    (several groups)
 * Conditions on other fields (e.g. rooms > 3) are kept as they are, joined with AND.
 */

export interface Ctx {
  rtKey?: string;
  catKey?: string;
  ptKey?: string;
  rts: string[];
  /** property types with their category code */
  pts: { code: string; cat: string }[];
  cats: { code: string; types: string[] }[];
}

export function makeCtx(fields: FormField[], index: number, lookups: Lookups): Ctx {
  const d = dimFields(fields, index);
  const catById = new Map(lookups.categories.map((c) => [c.id, c.code]));
  const pts = lookups.propertyTypes.map((t) => ({ code: t.code, cat: catById.get(t.category_id) ?? '' }));
  return {
    rtKey: d.request_type?.key,
    catKey: d.property_category?.key,
    ptKey: d.property_type?.key,
    rts: lookups.requestTypes.map((r) => r.code),
    pts,
    cats: lookups.categories.map((c) => ({ code: c.code, types: pts.filter((p) => p.cat === c.code).map((p) => p.code) })),
  };
}

const dimKeys = (c: Ctx) => new Set([c.rtKey, c.catKey, c.ptKey].filter(Boolean) as string[]);

function onlyDims(r: Rule, keys: Set<string>): boolean {
  if (!isGroup(r)) return keys.has((r as Condition).field);
  return [...(r.all ?? []), ...(r.any ?? []), ...(r.not ? [r.not] : [])].every((x) => onlyDims(x, keys));
}

/** dimension part (request/category/type) and everything else */
export function splitRule(rule: Rule | null | undefined, c: Ctx): { dim: Rule | null; rest: Rule[] } {
  if (!rule) return { dim: null, rest: [] };
  const keys = dimKeys(c);
  if (onlyDims(rule, keys)) return { dim: rule, rest: [] };
  if (isGroup(rule) && rule.all && !rule.any && !rule.not) {
    const d = rule.all.filter((x) => onlyDims(x, keys));
    const rest = rule.all.filter((x) => !onlyDims(x, keys));
    return { dim: d.length === 0 ? null : d.length === 1 ? d[0]! : { all: d }, rest };
  }
  return { dim: null, rest: [rule] };
}

const list = (v: unknown) => (Array.isArray(v) ? v.map(String) : v === undefined || v === null ? [] : [String(v)]);

/** Evaluate only the request/category/type conditions; anything else counts as "met". */
function evalDim(r: Rule, vals: Record<string, string | undefined>): boolean {
  if (isGroup(r)) {
    if (r.all) return r.all.every((x) => evalDim(x, vals));
    if (r.any) return r.any.some((x) => evalDim(x, vals));
    if (r.not) return !evalDim(r.not, vals);
    return true;
  }
  const cond = r as Condition;
  if (!(cond.field in vals)) return true;
  const v = vals[cond.field];
  const set = list(cond.value);
  switch (cond.op) {
    case 'in':
    case 'eq':
      return v !== undefined && set.includes(v);
    case 'not_in':
    case 'neq':
      return v === undefined || !set.includes(v);
    case 'is_empty':
      return v === undefined;
    case 'is_not_empty':
      return v !== undefined;
    default:
      return true;
  }
}

/** visible[rt][pt] ('' as pt when the field can't depend on the property type) */
export type Table = Record<string, Record<string, boolean>>;

export function readTable(dim: Rule | null, c: Ctx): Table {
  const t: Table = {};
  const pts = c.ptKey ? c.pts : [{ code: '', cat: '' }];
  for (const rt of c.rtKey ? c.rts : ['']) {
    t[rt] = {};
    for (const p of pts) {
      const vals: Record<string, string | undefined> = {};
      if (c.rtKey) vals[c.rtKey] = rt;
      if (c.ptKey) vals[c.ptKey] = p.code;
      // the category follows the type; with no type field the category can't be known here
      if (c.catKey && c.ptKey) vals[c.catKey] = p.cat;
      t[rt]![p.code] = !dim || evalDim(dim, vals);
    }
  }
  return t;
}

/** `in` or `not_in`, whichever is shorter (not_in keeps future lookup rows included) */
function cond(field: string, chosen: string[], all: string[]): Condition | null {
  const inc = all.filter((x) => chosen.includes(x));
  const exc = all.filter((x) => !chosen.includes(x));
  if (exc.length === 0) return null;
  if (exc.length < inc.length) return { field, op: 'not_in', value: exc };
  return { field, op: 'in', value: inc };
}

/** property types → "category in …" when they are whole categories, else "type in …" */
function typeCond(types: string[], c: Ctx): Condition | null {
  if (!c.ptKey) return null;
  const allTypes = c.pts.map((p) => p.code);
  if (types.length === allTypes.length) return null;
  if (c.catKey) {
    const whole = c.cats.filter((k) => k.types.length > 0 && k.types.every((t) => types.includes(t)));
    const covered = new Set(whole.flatMap((k) => k.types));
    if (whole.length && types.every((t) => covered.has(t))) return cond(c.catKey, whole.map((k) => k.code), c.cats.map((k) => k.code));
  }
  return cond(c.ptKey, types, allTypes);
}

export function writeTable(t: Table, rest: Rule[], c: Ctx): Rule | null {
  const rts = Object.keys(t);
  const pts = Object.keys(t[rts[0] ?? ''] ?? {});
  // group the property types that are shown for the same request types
  const groups = new Map<string, { rts: string[]; types: string[] }>();
  for (const p of pts) {
    const on = rts.filter((rt) => t[rt]![p]);
    if (!on.length) continue;
    const k = on.join(',');
    const g = groups.get(k) ?? { rts: on, types: [] };
    g.types.push(p);
    groups.set(k, g);
  }
  let dim: Rule | null;
  if (groups.size === 0) {
    // shown for nobody (the builder reports it)
    dim = c.rtKey ? { field: c.rtKey, op: 'in', value: [] } : null;
  } else {
    const parts = [...groups.values()].map((g) => {
      const conds = [c.rtKey ? cond(c.rtKey, g.rts, c.rts) : null, c.ptKey ? typeCond(g.types, c) : null].filter(Boolean) as Condition[];
      return conds.length === 0 ? null : conds.length === 1 ? (conds[0] as Rule) : ({ all: conds } as Rule);
    });
    if (parts.some((x) => x === null)) dim = null; // one group = everyone
    else dim = parts.length === 1 ? parts[0]! : { any: parts as Rule[] };
  }
  const items: Rule[] = dim ? (isGroup(dim) && dim.all && !dim.any && !dim.not ? dim.all : [dim]) : [];
  items.push(...rest);
  return items.length === 0 ? null : items.length === 1 ? items[0]! : { all: items };
}
